import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, ArrowRight, ClipboardCheck, FilePlus2, PackageCheck, Scissors, ShoppingBag, Truck } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { ddmmyyyy, inr, num } from "../lib/format";

const Tile = ({ label, value, to, state, alert, testid }) => (
  <Link to={to} state={state} className={`panel rise flex flex-col justify-between gap-2 p-4 transition-transform hover:-translate-y-0.5 ${alert ? "border-destructive/50 bg-destructive/5" : ""}`} data-testid={testid}>
    <span className="mono-label text-[10px] text-muted-foreground">{label}</span>
    <span className={`mono text-2xl font-bold ${alert ? "text-destructive" : ""}`} data-testid={`${testid}-value`}>{value}</span>
  </Link>
);

const Section = ({ title, icon: Icon, children, testid }) => (
  <section className="space-y-2" data-testid={testid}>
    <h2 className="mono-label flex items-center gap-2 px-1 text-muted-foreground"><Icon size={14} /> {title}</h2>
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">{children}</div>
  </section>
);

const Action = ({ to, state, icon: Icon, label, testid }) => (
  <Link to={to} state={state} className="btn btn-outline justify-between" data-testid={testid}>
    <span className="flex items-center gap-2"><Icon size={18} /> {label}</span><ArrowRight size={16} />
  </Link>
);

const Actions = ({ can }) => (
  <div className="grid grid-cols-1 gap-2 sm:grid-cols-2" data-testid="dash-actions">
    {can("po_write") && <Action to="/new" icon={FilePlus2} label="Raise a new PO" testid="dash-action-new-po" />}
    {can("po_status") && <Action to="/pos" icon={ClipboardCheck} label="Review & approve POs" testid="dash-action-review" />}
    {can("grn_write") && <Action to="/grns" icon={PackageCheck} label="GRNs & LR tracking" testid="dash-action-grn" />}
    {can("jobs_write") && <Action to="/jobs/new" icon={Scissors} label="Issue fabric to fabricator" testid="dash-action-new-job" />}
    {can("stock_write") && <Action to="/challans/new" icon={Truck} label="New delivery challan" testid="dash-action-challan" />}
    {can("stock_write") && <Action to="/sales/new" icon={ShoppingBag} label="Record sale" testid="dash-action-sale" />}
  </div>
);

export default function Dashboard() {
  const { user, can } = useAuth();
  const [d, setD] = useState(null);
  useEffect(() => {
    api.get("/dashboard").then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e)));
  }, []);
  if (!d) return <p className="text-muted-foreground">Loading…</p>;
  const jobsTo = { pathname: "/jobs" };
  return (
    <div className="space-y-6" data-testid="dashboard">
      <div>
        <p className="mono-label text-brand" data-testid="dash-role">{d.role_label} · {ddmmyyyy(d.today)}</p>
        <h1 className="font-heading text-3xl font-bold tracking-tight">Hello, {user.name.split(" ")[0]}</h1>
      </div>
      {d.po && (
        <Section title="Purchase orders" icon={ClipboardCheck} testid="dash-po">
          <Tile label="Waiting for review" value={num(d.po.review)} to="/pos" alert={d.po.review > 0} testid="dash-po-review" />
          <Tile label="Draft POs" value={num(d.po.drafts)} to="/pos" testid="dash-po-drafts" />
          <Tile label="Past delivery date" value={num(d.po.overdue)} to="/pos" alert={d.po.overdue > 0} testid="dash-po-overdue" />
          <Tile label="Accepted this month" value={inr(d.po.accepted_month_value)} to="/pos" testid="dash-po-accepted" />
        </Section>
      )}
      {d.grn && (
        <Section title="Goods receipt" icon={PackageCheck} testid="dash-grn">
          <Tile label="POs awaiting GRN" value={num(d.grn.awaiting)} to="/pos" alert={d.grn.awaiting > 0} testid="dash-grn-awaiting" />
          <Tile label="LRs not delivered" value={num(d.grn.lr_open)} to="/grns" testid="dash-grn-lr" />
          <Tile label="GRNs this month" value={num(d.grn.month)} to="/grns" testid="dash-grn-month" />
        </Section>
      )}
      {d.jobs && (
        <Section title="Job work" icon={Scissors} testid="dash-jobs">
          <Tile label="Open job orders" value={num(d.jobs.open)} to={jobsTo} state={{ view: "jobs" }} testid="dash-jobs-open" />
          <Tile label="Shirts recd this month" value={num(d.jobs.shirts_month)} to={jobsTo} state={{ view: "summary" }} testid="dash-jobs-shirts" />
          <Tile label="Fabricator bills this month" value={num(d.jobs.bills_month)} to={jobsTo} state={{ view: "jobs" }} testid="dash-jobs-bills" />
        </Section>
      )}
      {d.stock && (
        <Section title="Shirts & pants stock" icon={ShoppingBag} testid="dash-stock">
          <Tile label="Shirts / pants in stock" value={`${num(d.stock.shirts)} / ${num(d.stock.pants)}`} to={jobsTo} state={{ view: "shirts" }} testid="dash-stock-total" />
          <Tile label="Below minimum" value={num(d.stock.low)} to={jobsTo} state={{ view: "shirts" }} alert={d.stock.low > 0} testid="dash-stock-low" />
          <Tile label="Challans in transit" value={num(d.stock.in_transit)} to={jobsTo} state={{ view: "shirts" }} testid="dash-stock-transit" />
          <Tile label="Sold today" value={d.stock.sold_today_value ? inr(d.stock.sold_today_value) : num(d.stock.sold_today)} to={jobsTo} state={{ view: "daily" }} testid="dash-stock-sold" />
        </Section>
      )}
      {(d.stock?.low > 0 || d.po?.review > 0) && (
        <p className="flex items-center gap-2 text-sm text-destructive" data-testid="dash-attention"><AlertTriangle size={16} /> Items in red need your attention.</p>
      )}
      <Actions can={can} />
    </div>
  );
}
